import { EntityDropdownInput } from "#src/batteries-included-components/Dropdowns/EntityDropdownInput";
import { useGetOneWorkflowTemplate } from "#src/components/hooks/adapters/useWorkflowTemplates";
import { useQuery } from "@tanstack/react-query";
import {
  DropdownInput,
  Form,
  Skeleton,
  useForm,
} from "@validereinc/common-components";
import {
  WorkflowCategoriesAdapter,
  WorkflowTemplatesAdapter,
  WorkflowTriggerWorkflowComplete,
} from "@validereinc/domain";
import React, { useEffect } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";

type WorkflowTriggerWorkflowCompleteFormValues = Pick<
  WorkflowTriggerWorkflowComplete,
  "workflow_template_id"
>;

const WorkflowConfigurationTriggerWorkflowCompletion = () => {
  const {
    setTriggerWorkflowCompletion,
    setTriggerWorkflowCompletionIsValid,
    getTriggerWorkflowCompletion,
  } = useWorkflowConfigurationFormStore();

  const storeValues = getTriggerWorkflowCompletion();

  const form = useForm<
    WorkflowTriggerWorkflowCompleteFormValues & {
      _workflow_category_id?: string;
    }
  >({
    values: storeValues,
  });

  const selectedCategory = form.watch("_workflow_category_id");

  const isFetchingCurrentWorkflowTemplateEnabled =
    Boolean(storeValues?.workflow_template_id) && !selectedCategory;

  const {
    data: currentWorkflowTemplateData,
    isLoading: isLoadingCurrentWorkflowTemplate,
  } = useGetOneWorkflowTemplate(
    { id: storeValues?.workflow_template_id ?? "" },
    {
      enabled: isFetchingCurrentWorkflowTemplateEnabled,
    }
  );

  const { data: formCategoriesOptions, isLoading: isLoadingCategories } =
    useQuery({
      queryFn: async () => {
        const { data } = await WorkflowCategoriesAdapter.getList({
          pageSize: 500,
        });
        return data.map((category) => ({
          id: category.id,
          name: category.name,
        }));
      },
      queryKey: ["workflow_category", "getList"],
    });

  useEffect(() => {
    if (currentWorkflowTemplateData?.data.category_id) {
      form.setValue(
        "_workflow_category_id",
        currentWorkflowTemplateData.data.category_id
      );
    }
  }, [currentWorkflowTemplateData?.data.category_id]);

  useEffect(() => {
    setTriggerWorkflowCompletionIsValid(form.formState.isValid);
  }, [form.formState.isValid, setTriggerWorkflowCompletionIsValid]);

  const isLoading =
    isLoadingCategories ||
    (isLoadingCurrentWorkflowTemplate &&
      isFetchingCurrentWorkflowTemplateEnabled);

  return (
    <Form {...form}>
      <Skeleton isLoading={isLoading}>
        <DropdownInput
          name="_workflow_category_id"
          options={formCategoriesOptions ?? []}
          labelKey="name"
          valueKey="id"
          placeholder="Select Category..."
          label="Workflow Category"
          isRequired
          isClearable={false}
          onChange={() => {
            if (storeValues) {
              setTriggerWorkflowCompletion({
                ...storeValues,
                workflow_template_id: "",
              });
            }
          }}
        />

        {selectedCategory && (
          <EntityDropdownInput
            isRequired
            isMulti={false}
            isClearable={false}
            fetchEntityList={WorkflowTemplatesAdapter.getList}
            label="Workflow Template"
            name="workflow_template_id"
            sort={{
              sortBy: "name",
              sortDirection: "asc",
            }}
            refetch={selectedCategory}
            fetchFilters={
              selectedCategory ? { category_id: selectedCategory } : {}
            }
            onChange={(workflow_template_id: string) => {
              if (storeValues) {
                setTriggerWorkflowCompletion({
                  ...storeValues,
                  workflow_template_id,
                });
              }
            }}
          />
        )}
      </Skeleton>
    </Form>
  );
};

export default WorkflowConfigurationTriggerWorkflowCompletion;
